import React, { useCallback, useMemo } from "react";
import { Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getEncipherencode } from "utils/encipherencode";
import Countdown from "react-countdown";
import { BeatLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import {
  addEmail,
  addPhone,
  getEmail,
  getPhone,
} from "features/user/userSlice";
import {
  getCustomerLoginMode,
  normalizeEmail,
  normalizePhoneNumber,
} from "./loginFieldConfig";
import OtpInputGroup from "./OtpInputGroup";
import useOtpFlow from "./useOtpFlow";
import { useTranslation } from "react-i18next";

function Otp({ inputValue, setConfirmOtp, response, setResponse }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const merchantConfig = useSelector(getMerchantConfig);
  const [params] = useSearchParams();
  const encipherencode = getEncipherencode(params);
  const userPhone = useSelector(getPhone);
  const userEmail = useSelector(getEmail);
  const rmsConfig = useSelector(getRmsConfig);
  const dispatch = useDispatch();
  const loginMode = getCustomerLoginMode(
    rmsConfig.customer_verification_method,
  );
  const otpDestination = useMemo(() => {
    const resolvedInputValue = String(inputValue || "");

    if (loginMode === "phone") {
      return normalizePhoneNumber(resolvedInputValue || userPhone);
    }

    if (loginMode === "email") {
      return normalizeEmail(resolvedInputValue || userEmail);
    }

    if (userPhone) {
      return userPhone;
    }

    if (resolvedInputValue.includes("@")) {
      return normalizeEmail(resolvedInputValue);
    }

    return resolvedInputValue || userEmail || "";
  }, [inputValue, loginMode, userEmail, userPhone]);
  const {
    otpDigits,
    setOtpDigits,
    otpError,
    isLoading,
    resend,
    errorType,
    otpResent,
    currentDate,
    handleOtpComplete,
    handleResend,
    markResendAvailable,
    renderer,
  } = useOtpFlow({
    response,
    setResponse,
    encipherencode,
    params,
    navigate,
    userEmail,
    userPhone,
  });
  const handleChangeIdentifier = useCallback(() => {
    setConfirmOtp(false);
    dispatch(addPhone(null));
    dispatch(addEmail(null));
  }, [dispatch, setConfirmOtp]);

  return (
    <section className="login-section otp-section">
      <div style={{ maxWidth: "450px" }}>
        <p className="return-title" style={{ marginBottom: "25px" }}>
          {rmsConfig.home_page_request_creation_title}
        </p>
        <p style={{ lineHeight: "1rem" }}>
          {t("login.otpIntro")}{" "}
          <span style={{ fontWeight: "600" }}>{otpDestination}</span>.{" "}
          <span
            style={{
              color: merchantConfig.primary_theme_color,
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: "600",
            }}
            onClick={handleChangeIdentifier}
          >
            {t("common.actions.change")}{" "}
            <BorderColorOutlinedIcon style={{ fontSize: "14px" }} />
          </span>
        </p>
        <p
          style={{
            marginTop: "40px",
            textAlign: "left",
          }}
        >
          {t("login.enterOtp")}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <OtpInputGroup
            value={otpDigits}
            onChange={setOtpDigits}
            onComplete={handleOtpComplete}
            themeColor={merchantConfig.primary_theme_color}
            hasError={otpError && !otpResent}
          />
          {(otpError || otpResent) && (
            <p
              style={{
                color: otpResent
                  ? "rgba(51, 51, 51, 0.7)"
                  : "rgba(221, 67, 93, 1)",
                marginTop: "10px",
                textAlign: "left",
              }}
            >
              {errorType}
            </p>
          )}
          <Button
            type="submit"
            variant="contained"
            className="login-action"
            disabled={isLoading}
            endIcon={
              isLoading ? (
                <span className="ui-label">
                  <BeatLoader color={merchantConfig.secondary_theme_color} />
                </span>
              ) : (
                <ArrowForwardIcon sx={{ position: "relative", top: "-1px" }} />
              )
            }
            color="primary"
            style={{
              width: "100%",
              borderRadius: !merchantConfig.use_rounded_edge_buttons
                ? "0px"
                : "10px",
              padding: "10px 1rem",
              fontSize: "16px",
              fontWeight: "600",
              marginTop: "20px",
            }}
            sx={{
              "& .MuiButton-endIcon": {
                marginLeft: "auto",
                marginRight: 0,
              },
            }}
          >
            {!isLoading && (
              <span className="ui-label">{t("common.actions.proceed")}</span>
            )}
          </Button>
        </form>
        <Button
          variant="text"
          color="primary"
          disabled={!resend}
          onClick={handleResend}
          style={{
            margin: "30px auto",
            textAlign: "center",
            fontSize: "16px",
            paddingLeft: "0px",
            color: "#000000B2",
          }}
        >
          <span className="ui-label" style={{ marginRight: "10px" }}>
            {t("common.actions.resendOtp")}{" "}
          </span>
          {!resend && (
            <span className="ui-label">
              <Countdown
                key={1}
                date={currentDate + 60000}
                renderer={renderer}
                onComplete={markResendAvailable}
              />
            </span>
          )}
        </Button>
      </div>
    </section>
  );
}

export default Otp;
